import React, { useCallback, useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { useMutation, useQuery } from "@apollo/client";

import styles from "./styles.module.scss";
import { ReactComponent as NotificationActiveIcon } from "../../SVGcomponents/notificationActive.svg";
import { ReactComponent as NotificationNotActiveIcon } from "../../SVGcomponents/notificationNotActive.svg";
import notificationsDark from "../../SVGcomponents/notificationsDark.svg";
import notificationsDarkNotActive from "../../SVGcomponents/notificationsDarkNotActive.svg";

import {
  GET_USER_NOTIFICATIONS,
  UPDATE_SEEN_NOTIFICATIONS,
} from "../../../api/graphql/users";
import { Notification } from "../../../types/notification";
import { NotificationsPopover } from "../../../sandbox/components";

const PAGE_SIZE = 10;

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  const day = date.getDate().toString().padStart(2, "0");
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  return `${day}.${month}`;
};

const formatTime = (dateString: string) => {
  const date = new Date(dateString);
  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");
  return `${hours}:${minutes}`;
};

/** Bell trigger + notifications popover anchored right under it. */
export const NotificationModal: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const loaderRef = useRef<HTMLDivElement | null>(null);

  const { data: notificationsData, refetch } = useQuery<{
    getUserNotifications: Notification[];
  }>(GET_USER_NOTIFICATIONS, { fetchPolicy: "network-only" });

  const [updateSeenNotifications] = useMutation(UPDATE_SEEN_NOTIFICATIONS, {
    refetchQueries: [{ query: GET_USER_NOTIFICATIONS }],
  });

  const sortedNotifications: Notification[] = (
    notificationsData?.getUserNotifications || []
  )
    .slice()
    .sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    );

  const hasUnread = sortedNotifications.some((n) => !n.isViewed);

  const handleMarkAllAsRead = useCallback(async () => {
    const unreadIds = sortedNotifications
      .filter((n) => !n.isViewed)
      .map((n) => n.id);

    if (unreadIds.length === 0) return;

    try {
      await updateSeenNotifications({
        variables: { notificationIds: unreadIds },
      });

      await refetch();
    } catch {
      // ignore
    }
  }, [sortedNotifications, updateSeenNotifications, refetch]);

  const closeModal = useCallback(() => {
    void handleMarkAllAsRead();
    setIsOpen(false);
  }, [handleMarkAllAsRead]);

  const toggleModal = () => {
    if (isOpen) {
      closeModal();
    } else {
      setIsOpen(true);
    }
  };

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [isOpen]);

  // Close on click outside the bell/popover
  useEffect(() => {
    if (!isOpen) return;

    const handleOutsideClick = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        closeModal();
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [isOpen, closeModal]);

  const totalCount = sortedNotifications.length;
  const visibleNotifications = sortedNotifications.slice(0, visibleCount);

  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const target = entries[0];

      if (!target?.isIntersecting) return;

      setVisibleCount((prev) => {
        if (prev < totalCount) {
          return Math.min(prev + PAGE_SIZE, totalCount);
        }

        return prev;
      });
    },
    [totalCount],
  );

  useEffect(() => {
    if (!isOpen) return;

    const option: IntersectionObserverInit = {
      root: null,
      rootMargin: "0px",
      threshold: 1.0,
    };

    const observer = new window.IntersectionObserver(handleObserver, option);

    if (loaderRef.current) observer.observe(loaderRef.current);

    return () => {
      if (loaderRef.current) observer.unobserve(loaderRef.current);
    };
  }, [handleObserver, isOpen]);

  return (
    <div ref={containerRef} className={styles.container}>
      <div
        className={classNames(styles.notificationIcon, {
          [styles.hasNotifications]: hasUnread,
        })}
        onClick={toggleModal}
      >
        {hasUnread ? (
          <>
            <NotificationActiveIcon
              key="active-light"
              className={styles.notificationsLight}
            />
            <img
              src={notificationsDark}
              alt="Notifications"
              className={styles.notificationsDark}
            />
          </>
        ) : (
          <>
            <NotificationNotActiveIcon
              key="notactive-light"
              className={styles.notificationsLight}
            />
            <img
              src={notificationsDarkNotActive}
              alt="Notifications"
              className={styles.notificationsDark}
            />
          </>
        )}
      </div>

      {isOpen && (
        <NotificationsPopover
          items={visibleNotifications.map((n) => ({
            id: n.id,
            text: n.message,
            date: formatDate(n.created_at),
            time: formatTime(n.created_at),
            read: n.isViewed,
          }))}
          loaderRef={loaderRef}
          loadingMore={visibleNotifications.length < totalCount}
        />
      )}
    </div>
  );
};
